import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Directive,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import {
    DocumentBuiltinFlagDict,
    DocumentBuiltinFlags,
    DocumentBuiltinFlagsSettings,
    DocumentFlag,
    DocumentStatusSystem,
    SalesDocumentType,
    SalesDocument,
} from '@icc/b2b/shared';
import { TranslateService } from '@ngx-translate/core';

export interface ChangedFlagEvent {
    flag: string;
    value: boolean;
    builtin: boolean;
}

interface FlagBreakpoint {
    element: HTMLElement;
    maxWidth: number;
    flag: DocumentFlag & {
        kind: 'company' | 'builtin';
    };
}

@Directive({
    selector: '[iccFlagElement]',
})
export class IccFlagElementComponentDirective {
    @Input() iccFlagElement: DocumentFlag & {
        kind: 'company' | 'builtin';
    };

    constructor(public elementRef: ElementRef) {}
}

@Component({
    selector: 'icc-document-flags',
    templateUrl: './document-flags.component.html',
    styleUrls: ['./document-flags.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentFlagsComponent implements AfterViewChecked, OnChanges {
    @Input() document: SalesDocument;
    @Input() group = 1;
    @Input() statusSystem: DocumentStatusSystem;
    @Input() side: 'seller' | 'buyer' = 'buyer';
    documentFlags: (DocumentFlag & {
        kind: 'company' | 'builtin';
    })[] = [];
    visibleFlags: (DocumentFlag & {
        kind: 'company' | 'builtin';
    })[] = [];
    overflowFlags: (DocumentFlag & {
        kind: 'company' | 'builtin';
    })[] = [];

    @Output() change = new EventEmitter<ChangedFlagEvent>();

    breakpoints: FlagBreakpoint[] = [];
    overflowItemWidth = 0;

    appearance: 'horizontal' | 'vertical' | 'hidden' = 'vertical';

    @HostBinding('class.icc-flags-vertical')
    get vertical() {
        return this.appearance === 'vertical';
    }

    @HostBinding('class.icc-flags-hidden')
    get hidden() {
        return this.appearance === 'hidden' || this.documentFlags?.length === 0;
    }

    @ViewChildren(IccFlagElementComponentDirective)
    flagElements!: QueryList<IccFlagElementComponentDirective>;
    @ViewChild('origin', {read: ElementRef}) buttonElement!: ElementRef<HTMLElement>;

    constructor(private elementRef: ElementRef, private translateService: TranslateService, private cdr: ChangeDetectorRef) {}

    ngOnInit(): void {}

    ngOnChanges() {
        if (this.document && this.statusSystem) {
            this.appearance =
                this.group === 2
                    ? this.statusSystem.system.flagGroup2Visibility
                    : this.statusSystem.system.flagGroup1Visibility;
            this.loadFlags();
        }
    }

    ngAfterViewChecked() {
        if (this.document && this.statusSystem && this.documentFlags.length) {
            this.refreshSizes();
            this.refreshMenu();
        }
    }

    loadFlags() {
        this.documentFlags = [];
        this.visibleFlags = [];
        this.overflowFlags = [];

        if (!this.document.flags) {
            this.document.flags = {};
        }

        const groupFlags = this.statusSystem.system.flagGroups[this.group];

        groupFlags.forEach((groupFlag) => {
            if (DocumentBuiltinFlagDict[groupFlag.code]) {
                this.documentFlags.push({
                    id: 0,
                    order: 0,
                    documentTypesIds: [this.document?.salesDocumentTypeId as SalesDocumentType],
                    code: groupFlag.code,
                    name: this.translateService.instant(DocumentBuiltinFlagDict[groupFlag.code]),
                    kind: 'builtin',

                    canBuyerActivate: false,
                    canBuyerDeactivate: false,
                    canSellerActivate: false,
                    canSellerDeactivate: false,
                    buyerSideVisibility: true,
                    resetAfterOrderAnnexation: true,
                    resetAfterQuotationCopying: true,
                    resetAfterQuotationModification: true,

                    ...DocumentBuiltinFlagsSettings[groupFlag.code]
                });
            } else {
                const flag = this.statusSystem.flags.find((el) => el.code === groupFlag.code);
                if (flag) {
                    this.documentFlags.push({
                        ...flag,
                        kind: 'company',
                    });
                }
            }
        });
        this.visibleFlags = [
            ...this.documentFlags
        ];
    }

    @HostListener('window:resize', [])
    onResize() {
        this.visibleFlags = [
            ...this.documentFlags
        ];
        this.overflowFlags = [];
        this.refreshSizes();
        this.refreshMenu();
    }

    changeFlag(value: boolean, flag: string, builtin = false) {
        this.change.emit({
            flag,
            value,
            builtin,
        });
    }

    showFlag(code: string) {
        const flag = this.documentFlags.find((el) => el.code === code);
        return this.side === 'seller' || flag?.buyerSideVisibility;
    }

    canFlagActivate(code: string) {
        const flag = this.documentFlags.find((el) => el.code === code);
        return (this.side === 'seller' && flag?.canSellerActivate) || flag?.canBuyerActivate;
    }
    canFlagDeactivate(code: string) {
        const flag = this.documentFlags.find((el) => el.code === code);
        return (this.side === 'seller' && flag?.canSellerDeactivate) || flag?.canBuyerDeactivate;
    }

    canFlagBeChanged(code: string, builtin = false) {
        const value = builtin ? this.document[code] : this.document.flags?.[code];
        return (!value && this.canFlagActivate(code)) || (value && this.canFlagDeactivate(code));
    }

    refreshSizes() {
        this.breakpoints = this.calculateBreakpoints(this.flagElements.toArray());

        const hasClass = this.buttonElement.nativeElement.classList.contains('overflow-active');
        if (!hasClass) {
            this.buttonElement.nativeElement.classList.add('overflow-active');
        }
        this.overflowItemWidth = this.calculateItemWidth(this.buttonElement.nativeElement);
        if (!hasClass) {
            this.buttonElement.nativeElement.classList.remove('overflow-active');
        }
    }

    private calculateBreakpoints(menuItems: IccFlagElementComponentDirective[]): FlagBreakpoint[] {
        const breakpoints: FlagBreakpoint[] = [];
        let currentMaxWidth = 0;

        menuItems.forEach((element) => {
            const elementWidth = this.calculateItemWidth(element.elementRef.nativeElement);
            currentMaxWidth += elementWidth + 16;

            breakpoints.push({
                element: element.elementRef.nativeElement,
                maxWidth: currentMaxWidth,
                flag: element.iccFlagElement,
            });
        });

        return breakpoints;
    }

    private calculateItemWidth(element: HTMLElement) {
        const width = element.getBoundingClientRect().width;
        return width;
    }

    private calculateContainerWidth(element: HTMLElement) {
        const width = element.getBoundingClientRect().width;
        return Math.floor(width);
    }

    refreshMenu() {
        let containerWidth = this.calculateContainerWidth(this.elementRef.nativeElement);
        const reversedBreakpoints = this.breakpoints.slice().reverse();
        let menuChanged = false;
        const isOverflowing =
            reversedBreakpoints.find(({ maxWidth }) => maxWidth > containerWidth) !== undefined;

        if (isOverflowing) {
            containerWidth -= this.overflowItemWidth;
        }

        reversedBreakpoints.forEach(({ element, maxWidth, flag }) => {
            const visibleFlagIndex = this.visibleFlags.indexOf(flag);
            if (maxWidth > containerWidth && visibleFlagIndex !== -1) {
                this.visibleFlags.splice(visibleFlagIndex, 1);
                this.overflowFlags.unshift(flag);
                menuChanged = true;
            }
        });

        if (this.overflowFlags.length > 0) {
            this.buttonElement.nativeElement.classList.add('overflow-active');
        } else {
            this.buttonElement.nativeElement.classList.remove('overflow-active');
        }

        if (menuChanged) {
            this.cdr.detectChanges();
        }
    }
}
